import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { calculateUserHours } from './Services/reportTimeWorked';


const data = [
  { name: 'Rent', billableHours: 44, nonBillableHours: 24 },
  { name: 'Utilities', billableHours: 55, nonBillableHours: 39 },
  { name: 'Groceries', billableHours: 13, nonBillableHours: 30 },
  { name: 'Transport', billableHours: 43, nonBillableHours: 50 },
  { name: 'Entertainment', billableHours: 46, nonBillableHours: 27 },
  { name: 'Healthcare', billableHours: 55, nonBillableHours: 40 },
  { name: 'Miscellaneous', billableHours: 32, nonBillableHours: 18 },
];

const UserHourGraph = ({ trackReport, trackReportLoading, trackReportError }) => {
  const reportingTimeWorked = useSelector((state) => state.reportingTimeWorked);

  const processedData = useMemo(() => {
    // return calculateUserHours(
    //   trackReport, reportingTimeWorked
    // );
    return calculateUserHours(trackReport, reportingTimeWorked).map(item => ({
      ...item,
      totalHours: item.billableHours + item.nonBillableHours
    }));
  }, [trackReport, reportingTimeWorked]);

  const changeDurationFormat = (sec) => {
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    const seconds = sec % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getTextWidth = (text, font = "12px Arial") => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = font;
    return context.measureText(text).width;
  };

  const getMaxNameWidth = (data, key) => {
    const longestName = data.reduce((max, item) =>
      item[key].length > max.length ? item[key] : max, ""
    );
    return getTextWidth(longestName);
  };

  const maxNameWidth = useMemo(() => getMaxNameWidth(processedData, "name"), [processedData]);

  if (trackReportLoading) {
    const skeletonRows = 8;
    const rowHeight = 50;
    const baseHeight = 100;
    const skeletonHeight = skeletonRows * rowHeight + baseHeight;
    
    // Fixed widths for skeleton bars to avoid random values
    const barWidths = [45, 60, 35, 55, 40, 50, 38, 48];
    
    return (
      <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, skeletonHeight) }}>
        <div style={{ 
          width: '100%', 
          height: '100%', 
          padding: '20px 30px 5px 0px',
          position: 'relative'
        }}>
          {/* Y-axis skeleton labels */}
          <div style={{ 
            position: 'absolute', 
            left: '0', 
            top: '20px', 
            bottom: '5px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingRight: '10px'
          }}>
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '120px',
                  height: '13px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>

          {/* Chart area with stacked bars */}
          <div style={{ 
            marginLeft: '150px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingTop: '10px',
            paddingBottom: '30px'
          }}>
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '50px',
                  position: 'relative'
                }}
              >
                {/* Non-billable hours bar skeleton (first/left) */}
                <div
                  style={{
                    width: `${(barWidths[index] || 45) - 15}%`,
                    height: '13px',
                    backgroundColor: '#C7B6F6',
                    opacity: 0.3,
                    borderRadius: '2px 0 0 2px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
                {/* Billable hours bar skeleton (second/right, stacked) */}
                <div
                  style={{
                    width: `${15}%`,
                    height: '13px',
                    backgroundColor: '#86ABE1',
                    opacity: 0.3,
                    borderRadius: '0 2px 2px 0',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: '0.2s'
                  }}
                />
              </div>
            ))}
          </div>

          {/* X-axis skeleton ticks */}
          <div style={{ 
            position: 'absolute', 
            bottom: '5px', 
            left: '150px', 
            right: '30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '40px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  if (trackReportError) return <div>Error loading data.</div>;

  const calculateXTicks = () => {
    if (!processedData.length) return [];
    const maxDataValue = Math.max(...processedData.map(item => Math.max(item.billableHours, item.nonBillableHours)));
    if (maxDataValue === 0) return [0];
    // const tickInterval = 10; // Set the interval between ticks
    const tickInterval = Math.ceil(maxDataValue / 10);
    const numOfTicks = Math.ceil(maxDataValue / tickInterval);
    const ticks = [];

    for (let i = 0; i <= numOfTicks + 4; i++) { // Adjust the number of ticks
      ticks.push(i * tickInterval);
    }

    return ticks;
  }
  // const xAxisTicks = calculateXTicks(processedData);
  const xAxisTicks = calculateXTicks();

  const calculateChartHeight = (dataLength) => {
    const rowHeight = 50; // Adjust this value to set the height per bar
    const baseHeight = 100; // Add some base height for padding and labels
    return dataLength * rowHeight + baseHeight;
  };

  return (
    <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, calculateChartHeight(processedData.length)), }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={processedData}
          barSize={13}
          margin={{
            top: 20,
            right: 30,
            left: maxNameWidth - 50,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis
            type="number"
            domain={[0, 'dataMax + 10']}
            tickFormatter={(tick) => changeDurationFormat(tick)}
            ticks={xAxisTicks}
          />
          <YAxis
            type="category"
            dataKey="name"
            // width={100}
            tickMargin={10} 
          />
          <Tooltip formatter={(value) => changeDurationFormat(value)} />
          <Legend />
          <Bar dataKey="nonBillableHours" name="Non-billable hours" stackId="a" fill="#C7B6F6">
            <LabelList
              dataKey="nonBillableHours"
              position="right"
              formatter={(value, entry) => {
                if (entry && entry.payload && entry.payload.billableHours === 0) {
                  return changeDurationFormat(value);
                }
                return null;
              }}
            />
          </Bar>
          <Bar dataKey="billableHours" name="Billable hours" stackId="a" fill="#86ABE1">
            {/* <LabelList dataKey="billableHours" position="right" formatter={(value) => changeDurationFormat(value)} /> */}
            <LabelList dataKey="totalHours" position="right" formatter={(value) => changeDurationFormat(value)} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default UserHourGraph;
